import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  FaAngleLeft,
  FaCircleCheck,
  FaCircleExclamation,
  FaCircleXmark,
  FaTriangleExclamation,
} from "react-icons/fa6";
import MontacargasNavbar from "../components/MontacargasNavbar";
import { getLiftUser, clearLiftSession } from "../services/liftSession";
import { getAuditor, uploadLoadsFile, updateLoadHeight, deleteLoad, validateLoads } from "../services/liftApi";
import "./auditor.css";

const ACCEPTED_FILES = ".xlsx,.xls,.csv";
const FILL_ALL_FIELDS = "Se deben llenar todos los campos";

// Tabla de referencia de index.blade.php: peso mínimo por altura y tarima.
const REFERENCE_ROWS = [
  ["Hasta 50cm", "+100kg", "+80kg"],
  ["Hasta 100cm", "+200kg", "+160kg"],
  ["Hasta 160cm", "+320kg", "+256kg"],
];

const ALERT_ICONS = {
  success: FaCircleCheck,
  warning: FaTriangleExclamation,
  danger: FaCircleExclamation,
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

// Sin separador de miles, igual que el PHP (y que el peso de la guía).
const formatNumber = (value) => String(Math.round(toNumber(value) * 1000) / 1000);

// Solo dígitos (Numeros() de index.blade.php).
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

function Alert({ alert, id }) {
  if (!alert) return null;
  const Icon = ALERT_ICONS[alert.level] || FaCircleExclamation;

  return (
    <div id={id} className={`mc-alert mc-alert--${alert.level || "danger"}`} role={alert.level === "success" ? "status" : "alert"}>
      <Icon aria-hidden="true" />
      <span>{alert.message}</span>
    </div>
  );
}

// Réplica de amcmontacargas-L8.8 index.blade.php ("Carga insumo"): datos de
// la guía, carga del archivo de la báscula, tabla de mediciones (piezas,
// altura, eliminar) y "Validar mediciones". Si la carga ya fue validada
// (first_val = 1) la vista queda de solo lectura, igual que en PHP.
function MontacargasAuditor() {
  const navigate = useNavigate();
  const { awb } = useParams();
  const user = getLiftUser();
  const fileInputRef = useRef(null);
  const validationAlertRef = useRef(null);

  const [auditor, setAuditor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [palletId, setPalletId] = useState("");
  const [heightId, setHeightId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadAlert, setUploadAlert] = useState(null);

  // Piezas medidas por medición (en PHP el input arranca en 1).
  const [estibas, setEstibas] = useState({});
  const [rowBusy, setRowBusy] = useState({});
  const [tableAlert, setTableAlert] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationAlert, setValidationAlert] = useState(null);

  const applyAuditor = useCallback((data) => {
    setAuditor(data);
    setEstibas((previous) => {
      const next = {};
      data.loads.forEach((load) => {
        next[load.id] = previous[load.id] ?? (data.first_val === 0 ? "1" : String(load.estiba ?? ""));
      });
      return next;
    });
  }, []);

  const fetchAuditor = useCallback(async () => {
    const response = await getAuditor({ awb });
    applyAuditor(response.data);
  }, [awb, applyAuditor]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");

    getAuditor({ awb })
      .then((response) => {
        if (active) applyAuditor(response.data);
      })
      .catch((error) => {
        if (!active || error.response?.status === 401) return;
        setLoadError(
          error.response?.status === 404
            ? error.response.data?.message || "Esta guia no es de exportación, favor de validar"
            : "No se pudo cargar la guía, intenta de nuevo.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [awb, applyAuditor]);

  useEffect(() => {
    if (validationAlert) validationAlertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [validationAlert]);

  const handleLogout = () => {
    clearLiftSession();
    navigate("/montacargas", { replace: true });
  };

  const isEditable = auditor?.first_val === 0;

  // Peso medido = suma de pesos de la tabla; piezas medidas = suma de los
  // inputs (o todas las piezas si ya se validó), como calcularSumatoria().
  const measuredWeight = useMemo(
    () => (auditor ? auditor.loads.reduce((sum, load) => sum + toNumber(load.gross_weight), 0) : 0),
    [auditor],
  );
  const measuredPieces = useMemo(() => {
    if (!auditor) return 0;
    if (!isEditable) return auditor.pieces;
    return auditor.loads.reduce((sum, load) => sum + toNumber(estibas[load.id]), 0);
  }, [auditor, estibas, isEditable]);

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
    setUploadAlert(null);
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    setUploadAlert(null);

    if (!palletId || !heightId || !file) {
      setUploadAlert({ level: "danger", message: FILL_ALL_FIELDS });
      return;
    }

    setUploading(true);
    try {
      const response = await uploadLoadsFile({ awbMaster: auditor.awb_master, palletId, heightId, file });
      applyAuditor(response.data.auditor);
      setUploadAlert({ level: "success", message: response.message || "Archivo cargado con éxito" });
      setValidationAlert(null);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      if (error.response?.status !== 401) {
        setUploadAlert({
          level: "danger",
          message: error.response?.data?.message || "No se pudo cargar el archivo, intenta de nuevo.",
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleHeightChange = async (load, value) => {
    const nextHeight = Number(value);
    const previousHeight = load.height_id;
    setTableAlert(null);
    setRowBusy((busy) => ({ ...busy, [load.id]: true }));
    setAuditor((current) => ({
      ...current,
      loads: current.loads.map((item) => (item.id === load.id ? { ...item, height_id: nextHeight } : item)),
    }));

    try {
      await updateLoadHeight({ loadId: load.id, heightId: nextHeight });
    } catch (error) {
      setAuditor((current) => ({
        ...current,
        loads: current.loads.map((item) => (item.id === load.id ? { ...item, height_id: previousHeight } : item)),
      }));
      if (error.response?.status !== 401) {
        setTableAlert({ level: "danger", message: error.response?.data?.message || "No se pudo actualizar la altura." });
      }
    } finally {
      setRowBusy((busy) => ({ ...busy, [load.id]: false }));
    }
  };

  const handleDelete = async (load) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Eliminar la medición de ${load.gross_weight} kg?`)) return;

    setTableAlert(null);
    setRowBusy((busy) => ({ ...busy, [load.id]: true }));
    try {
      await deleteLoad({ loadId: load.id });
      await fetchAuditor();
    } catch (error) {
      if (error.response?.status !== 401) {
        setTableAlert({ level: "danger", message: error.response?.data?.message || "No se pudo eliminar la medición." });
      }
    } finally {
      setRowBusy((busy) => ({ ...busy, [load.id]: false }));
    }
  };

  const handleValidate = async () => {
    setValidationAlert(null);
    setTableAlert(null);

    const rows = auditor.loads.map((load) => ({ id: load.id, estiba: estibas[load.id] ?? "" }));
    if (rows.some((row) => row.estiba === "")) {
      setValidationAlert({ level: "danger", message: FILL_ALL_FIELDS });
      return;
    }

    setValidating(true);
    try {
      const response = await validateLoads({ masterId: auditor.master_id, rows });
      applyAuditor(response.data.auditor);
      setValidationAlert({ level: response.data.level, message: response.message });
      setUploadAlert(null);
    } catch (error) {
      if (error.response?.status !== 401) {
        setValidationAlert({
          level: "danger",
          message: error.response?.data?.message || "No se pudieron validar las mediciones, intenta de nuevo.",
        });
      }
    } finally {
      setValidating(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="mc-auditor__state">
          <Spinner animation="border" role="status" />
          <span>Cargando guía...</span>
        </div>
      );
    }

    if (loadError || !auditor) {
      return (
        <div className="mc-auditor__state">
          <Alert alert={{ level: "danger", message: loadError || "No se pudo cargar la guía." }} />
          <button type="button" className="mc-btn-primary" onClick={() => navigate("/montacargas/home")}>
            Regresar
          </button>
        </div>
      );
    }

    const { tarimaOptions, heightOptions } = auditor;

    return (
      <>
        <section className="mc-master" aria-label="Datos de la guía master">
          <p className="mc-master__title">
            <span className="mc-master__key">Master:</span>{" "}
            <span className="mc-master__awb">{auditor.awb_master}</span>
          </p>
          <dl className="mc-master__grid">
            {[
              ["Descripción", auditor.description],
              ["Origen", auditor.origin],
              ["Destino", auditor.destiny],
              ["Piezas", auditor.pieces],
              ["Peso Medido", `${formatNumber(measuredWeight)} de ${auditor.weight ?? 0}`],
              ["Volumen", auditor.volume],
              ["Tipo de mercancía", auditor.merchandise_type],
              ["Piezas Medidas", `${measuredPieces} de ${auditor.pieces ?? 0}`],
              ["Piezas Validadas", `${auditor.estibas_validadas} de ${auditor.pieces ?? 0}`],
            ].map(([label, value]) => (
              <div className="mc-master__item" key={label}>
                <dt className="mc-master__key">{label}:</dt>
                <dd className="mc-master__value">{value ?? "—"}</dd>
              </div>
            ))}
          </dl>
        </section>

        {isEditable && (
          <form className="mc-upload" onSubmit={handleUpload} noValidate>
            <div className="mc-upload__selects">
              <select
                className="form-select mc-select"
                aria-label="Tipo de tarima"
                value={palletId}
                onChange={(event) => {
                  setPalletId(event.target.value);
                  setUploadAlert(null);
                }}
                disabled={uploading}
              >
                <option value="" disabled>
                  Tipo de tarima
                </option>
                {Object.entries(tarimaOptions).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="form-select mc-select"
                aria-label="Rango de alturas"
                value={heightId}
                onChange={(event) => {
                  setHeightId(event.target.value);
                  setUploadAlert(null);
                }}
                disabled={uploading}
              >
                <option value="" disabled>
                  Rango de alturas
                </option>
                {Object.entries(heightOptions).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mc-upload__box">
              <div className="mc-upload__file">
                <input
                  ref={fileInputRef}
                  id="montacargas-ravas"
                  type="file"
                  accept={ACCEPTED_FILES}
                  className="mc-upload__input"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <label htmlFor="montacargas-ravas" className="mc-btn-upload">
                  Subir archivo
                </label>
                <p className="mc-upload__filename">{file ? file.name : "Selecciona el archivo a cargar"}</p>
              </div>

              <table className="mc-reference">
                <thead>
                  <tr>
                    <th aria-hidden="true" />
                    <th colSpan={2}>Tipo de tarima</th>
                  </tr>
                  <tr>
                    <th>Altura</th>
                    <th>Std</th>
                    <th>Europea</th>
                  </tr>
                </thead>
                <tbody>
                  {REFERENCE_ROWS.map(([label, std, europe]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td>{std}</td>
                      <td>{europe}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert alert={uploadAlert} />

            <button type="submit" className="mc-btn-primary" disabled={uploading}>
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Cargando...
                </>
              ) : (
                "Cargar insumo"
              )}
            </button>
          </form>
        )}

        <section className="mc-loads" aria-label="Mediciones">
          <div className="mc-loads__scroll">
            <table className={`mc-loads__table${isEditable ? " mc-loads__table--editable" : ""}`}>
              <thead>
                <tr>
                  <th>Piezas medidas</th>
                  <th>Peso</th>
                  <th>Tipo de tarima</th>
                  <th>Rango de alturas</th>
                  {isEditable && <th>Eliminar medición</th>}
                </tr>
              </thead>
              <tbody>
                {auditor.loads.length === 0 && (
                  <tr>
                    <td colSpan={isEditable ? 5 : 4} className="mc-loads__empty">
                      Sin mediciones cargadas
                    </td>
                  </tr>
                )}
                {auditor.loads.map((load) => (
                  <tr key={load.id}>
                    <td className="mc-loads__cell--pieces" data-label="Piezas medidas">
                      {isEditable ? (
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control mc-loads__pieces"
                          aria-label={`Piezas medidas de la medición de ${load.gross_weight} kg`}
                          placeholder="0"
                          value={estibas[load.id] ?? ""}
                          onChange={(event) => {
                            const value = onlyDigits(event.target.value);
                            setEstibas((current) => ({ ...current, [load.id]: value }));
                            setValidationAlert(null);
                          }}
                          disabled={validating}
                        />
                      ) : (
                        load.estiba
                      )}
                    </td>
                    <td className="mc-loads__cell--weight" data-label="Peso">
                      {load.gross_weight}
                    </td>
                    <td className="mc-loads__cell--pallet" data-label="Tarima">
                      {tarimaOptions[load.pallet_id] ?? (Number(load.pallet_id) === 2 ? "Europea" : "Std")}
                    </td>
                    <td className="mc-loads__cell--height" data-label="Rango de alturas">
                      {isEditable ? (
                        <select
                          className="form-select mc-loads__height"
                          aria-label={`Rango de alturas de la medición de ${load.gross_weight} kg`}
                          value={load.height_id}
                          onChange={(event) => handleHeightChange(load, event.target.value)}
                          disabled={rowBusy[load.id] || validating}
                        >
                          {Object.entries(heightOptions).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        heightOptions[load.height_id]
                      )}
                    </td>
                    {isEditable && (
                      <td className="mc-loads__cell--delete">
                        <button
                          type="button"
                          className="mc-loads__delete"
                          aria-label={`Eliminar la medición de ${load.gross_weight} kg`}
                          onClick={() => handleDelete(load)}
                          disabled={rowBusy[load.id] || validating}
                        >
                          <FaCircleXmark aria-hidden="true" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <Alert alert={tableAlert} />

        <div ref={validationAlertRef}>
          <Alert alert={validationAlert} />
        </div>

        {isEditable && (
          <div className="mc-auditor__actions">
            <button type="button" className="mc-btn-primary" onClick={handleValidate} disabled={validating}>
              {validating ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Validando...
                </>
              ) : (
                "Validar mediciones"
              )}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="mc-page">
      <MontacargasNavbar title="Carga insumo">
        <button type="button" className="mc-navbar-btn mc-navbar-btn--back" onClick={() => navigate("/montacargas/home")}>
          <FaAngleLeft aria-hidden="true" />
          Regresar
        </button>
        <button type="button" className="mc-navbar-btn" onClick={handleLogout}>
          Cerrar sesión
        </button>
      </MontacargasNavbar>

      <main className="mc-page__content mc-auditor">
        <h2 className="mc-page__welcome">Bienvenid@{user?.name ? `, ${user.name}` : ""}</h2>
        {renderBody()}
      </main>
    </div>
  );
}

export default MontacargasAuditor;
